import { PasswordForm, PasswordLinkView } from "./components/PasswordSettings";
import { AccountInformation } from "./components/AccountInformation";
import { RecommendationView } from "./components/RecommendationView";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { ContentPageView } from "./components/ContentPage";
import { Login } from "./components/Login";
import { api, ApiError, onUnauthorized, onRestricted } from "./lib/api";
import type { Account, NavigationNode } from "./lib/types";
import { RoleLabelsProvider } from "./lib/RoleLabelsContext";
import { SessionPanel } from "./components/SessionPanel";
import { useSoloWhiteboard } from "./whiteboard/SoloWhiteboardContext";
import { navigationWithoutLegacyStartPage, StartPage } from "./components/StartPage";
import { SubjectOverview } from "./components/SubjectOverview";
import { useRealtime } from "./realtime/RealtimeContext";
import { roleLabel, technicalVersion } from "@calega/versioning";
import { LegalPageView } from "./components/LegalPage";
import type { LegalPageSlug } from "@calega/content";
import { PublicHome } from "./components/PublicHome";
import { AdaptiveAssignmentView } from "./components/AdaptiveAssignmentView";
import { IndividualAssignmentView } from "./components/IndividualAssignmentView";
import { LogoutButton } from "./components/LogoutButton";
import { TherapistHeaderActions } from "./components/TherapistHeaderActions";
import { SessionActivity } from "./components/SessionActivity";
import { exerciseRunStartedEvent } from "./exercises/learning-api";

const Flashcards = lazy(() => import("./components/Flashcards").then((module) => ({ default: module.Flashcards })));
const AdminCms = lazy(() => import("./components/AdminCms").then((module) => ({ default: module.AdminCms })));
const ManagementPortal = lazy(() => import("./components/ManagementPortal").then((module) => ({ default: module.ManagementPortal })));
const LearningDashboard = lazy(() => import("./components/LearningDashboard").then((module) => ({ default: module.LearningDashboard })));
const LearnerHistory = lazy(() => import("./components/LearnerHistory").then((module) => ({ default: module.LearnerHistory })));
const QualitativeAnalysis = lazy(() => import("./components/QualitativeAnalysis").then((module) => ({ default: module.QualitativeAnalysis })));
const IndividualExercises = lazy(() => import("./components/IndividualExercises").then((module) => ({ default: module.IndividualExercises })));
const Worksheets = lazy(() => import("./components/Worksheets").then((module) => ({ default: module.Worksheets })));
const WhiteboardWorkspace = lazy(() => import("./whiteboard/WhiteboardWorkspace").then((module) => ({ default: module.WhiteboardWorkspace })));

export function parseRoute(value: string) {
  const hash = value.replace(/^#\/?/, "");
  if (hash === "admin/cms") return { kind: "cms" as const };
  if (hash === "management") return { kind: "management" as const };
  if (hash === "account") return { kind: "account" as const };
  if (hash === "flashcards" || hash === "page/karteikarten") return { kind: "flashcards" as const };
  if (hash === "learning" || hash === "learning/history") return { kind: "learning" as const, section: hash === "learning/history" ? "history" as const : undefined };
  if (hash.startsWith("learning/history/")) return { kind: "learning" as const, section: "history" as const, runId: decodeURIComponent(hash.slice("learning/history/".length)) };
  if (hash === "individual-exercises") return { kind: "individual-exercises" as const };
  if (hash === "worksheets") return { kind: "worksheets" as const };
  if (hash === "whiteboard") return { kind: "whiteboard" as const };
  if (hash === "analysis") return { kind: "analysis" as const, runId: undefined };
  if (hash.startsWith("analysis/")) return { kind: "analysis" as const, runId: decodeURIComponent(hash.slice("analysis/".length)) };
  if (hash.startsWith("recommendation/")) return { kind: "recommendation" as const, id: decodeURIComponent(hash.slice("recommendation/".length)) };
  if (hash.startsWith("adaptive-assignment/")) return { kind: "adaptive-assignment" as const, id: decodeURIComponent(hash.slice("adaptive-assignment/".length)) };
  if (hash.startsWith("individual-assignment/")) return { kind: "individual-assignment" as const, id: decodeURIComponent(hash.slice("individual-assignment/".length)) };
  if (hash.startsWith("subject/")) return { kind: "subject" as const, id: decodeURIComponent(hash.slice(8)) };
  if (hash === "legal/impressum" || hash === "legal/datenschutz") {
    return { kind: "legal" as const, slug: hash.slice(6) as LegalPageSlug };
  }
  if (hash.startsWith("page/")) return { kind: "page" as const, slug: decodeURIComponent(hash.slice(5)) };
  return { kind: "home" as const };
}

function currentRoute() {
  return parseRoute(window.location.hash);
}

export function isLoginPath(value: string) {
  return /^\/login(?:\/[^/]+)?\/?$/i.test(value);
}

export function isLoginRequest(pathname: string, search: string, hash = "") {
  return isLoginPath(pathname)
    || new URLSearchParams(search).has("qr_login")
    || new URLSearchParams(hash.replace(/^#/, "")).has("qr_login");
}

function NavigationPageButton({ page, activeSlug, onNavigate }: { page: NavigationNode; activeSlug?: string; onNavigate: (slug: string) => void }) {
  return (
    <button
      className={page.pageSlug === activeSlug ? "active" : ""}
      onClick={() => page.pageSlug && onNavigate(page.pageSlug)}
    >
      <span>{page.pageType === "EXERCISE" ? "✏️" : "📖"}</span>
      {page.title}
    </button>
  );
}

function Navigation({ items, activeSlug, activeSubjectId, onNavigate, onNavigateSubject }: { items: NavigationNode[]; activeSlug?: string; activeSubjectId?: string; onNavigate: (slug: string) => void; onNavigateSubject: (id: string) => void }) {
  return (
    <nav className="sidebar-navigation" aria-label="Lerninhalte">
      {items.map((subject) => (
        <section className="nav-subject" key={subject.id}>
          <button
            className={subject.id === activeSubjectId ? "nav-subject-heading active" : "nav-subject-heading"}
            aria-current={subject.id === activeSubjectId ? "page" : undefined}
            onClick={() => onNavigateSubject(subject.id)}
          >
            <span>{subject.icon}</span>
            <div>
              <strong>{subject.title}</strong>
              {subject.description && <small>{subject.description}</small>}
            </div>
          </button>
          {subject.children.map((node) => node.type === "PAGE" ? (
            <div className="nav-pages nav-direct-pages" key={node.id}>
              <NavigationPageButton page={node} activeSlug={activeSlug} onNavigate={onNavigate} />
            </div>
          ) : (
            <details className="nav-topic" key={node.id} open>
              <summary>{node.title}</summary>
              <div className="nav-pages">
                {node.children.map((page) => (
                  <NavigationPageButton page={page} activeSlug={activeSlug} onNavigate={onNavigate} key={page.id} />
                ))}
              </div>
            </details>
          ))}
        </section>
      ))}
    </nav>
  );
}

export default function App() {
  const realtime = useRealtime();
  const solo = useSoloWhiteboard();
  const [account, setAccount] = useState<Account | null>(null);
  const [navigation, setNavigation] = useState<NavigationNode[]>([]);
  const [route, setRoute] = useState(currentRoute);
  const [passwordLinkToken, setPasswordLinkToken] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("password_link"));
  const [passwordNotice, setPasswordNotice] = useState(false);
  const [idleLogoutNotice, setIdleLogoutNotice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [actingOptions, setActingOptions] = useState<Array<{ id: string; displayName: string }>>([]);

  useEffect(() => {
    if (passwordLinkToken) window.history.replaceState({}, "", window.location.pathname + window.location.search);
    const listener = () => {
      const token = new URLSearchParams(window.location.hash.slice(1)).get("password_link");
      if (token) { setPasswordLinkToken(token); window.history.replaceState({}, "", window.location.pathname + window.location.search); }
      setRoute(currentRoute());
    };
    const clearSession = (error: ApiError) => {
      if (error.code === "SESSION_IDLE_TIMEOUT") return endIdleSession();
      realtime.disconnect();
      setAccount(null);
      setNavigation([]);
      setActingOptions([]);
      setSidebarOpen(false);
      setSidebarCollapsed(false);
    };
    let validationInFlight = false;
    const validateSession = () => {
      if (validationInFlight) return;
      validationInFlight = true;
      api<{ account: Account }>("/auth/me")
        .then((result) => setAccount(result.account))
        .catch((error) => {
          if (!(error instanceof ApiError) || error.status !== 401) console.error(error);
        })
        .finally(() => {
          validationInFlight = false;
          setLoading(false);
        });
    };
    const validateVisibleSession = () => {
      if (document.visibilityState === "visible") validateSession();
    };
    window.addEventListener("hashchange", listener);
    window.addEventListener("focus", validateSession);
    document.addEventListener("visibilitychange", validateVisibleSession);
    const unsubscribeUnauthorized = onUnauthorized(clearSession);
    const restrictSession = () => {
      realtime.disconnect(); setNavigation([]); setActingOptions([]);
      setAccount((current) => current ? { ...current, actingAs: undefined, subscription: { current: null, scheduled: null, paused: false, usage: { therapists: 0, learners: 0, administrators: 0 }, reasons: [], ...current.subscription, restricted: true } } : null);
      validateSession();
    };
    const unsubscribeRestricted = onRestricted(restrictSession);
    window.addEventListener("calepia:subscription-restricted", restrictSession);
    const subscriptionTimer = window.setInterval(validateVisibleSession, 10_000);
    validateSession();
    return () => {
      window.clearInterval(subscriptionTimer);
      unsubscribeRestricted();
      window.removeEventListener("calepia:subscription-restricted", restrictSession);
      window.removeEventListener("hashchange", listener);
      window.removeEventListener("focus", validateSession);
      document.removeEventListener("visibilitychange", validateVisibleSession);
      unsubscribeUnauthorized();
    };
  }, []);

  useEffect(() => {
    const collapseNavigation = () => {
      setSidebarOpen(false);
      setSidebarCollapsed(true);
    };
    window.addEventListener(exerciseRunStartedEvent, collapseNavigation);
    return () => window.removeEventListener(exerciseRunStartedEvent, collapseNavigation);
  }, []);

  useEffect(() => {
    if (!account || account.subscription?.restricted) { realtime.disconnect(); setNavigation([]); return; }
    api<{ items: NavigationNode[] }>("/content/navigation").then((result) => {
      setNavigation(navigationWithoutLegacyStartPage(result.items));
    });
  }, [account?.id, account?.subscription?.restricted]);

  useEffect(() => {
    if (!account || account.subscription?.restricted || !account.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST")) {
      setActingOptions([]);
      return;
    }
    api<Array<{ id: string; displayName: string }>>("/auth/acting-as/options").then(setActingOptions).catch(() => setActingOptions([]));
  }, [account?.id, account?.subscription?.restricted]);

  const activeSlug = route.kind === "page" ? route.slug : undefined;
  const activeSubject = route.kind === "subject" ? navigation.find((subject) => subject.id === route.id) : undefined;
  const canUseCms = account?.roles.includes("ADMIN") ?? false;
  const canUseManagement = account?.roles.includes("ADMIN")
    || Boolean(account?.modules.accounts && account?.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST"));
  const canUseLearningDashboard = Boolean(account?.actingAs || account?.roles.includes("LEARNER"));
  const canUseQualitativeAnalysis = Boolean(account?.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST"));
  const canUseIndividualExercises = Boolean(account?.modules.individualExercises && account.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST"));
  const canUseWorksheets = Boolean(account && !account.roles.includes("ADMIN") && !account.roles.includes("LEARNER") && account.organizations.some(({ id, role }) => id === account.organizationId && ["OWNER", "MANAGER", "THERAPIST"].includes(role)));
  const roleText = useMemo(() => account?.roles.map((role) => role === "ADMIN" ? "Admin" : roleLabel(account.roleLabels, role)).join(" · "), [account]);
  const usesTherapistHeader = Boolean(account?.roles.includes("THERAPIST") && !account.roles.includes("ADMIN"));
  useEffect(() => {
    if (!account || !canUseWorksheets || account.subscription?.restricted) solo.reset(); else void solo.restore();
  }, [account?.id, canUseWorksheets, account?.subscription?.restricted]);
  useEffect(() => {
    if (route.kind !== "whiteboard" || !account || !canUseWorksheets || realtime.session) return;
    void solo.start(account.actingAs ? { kind: "learner", learnerId: account.actingAs.id } : { kind: "personal" }).catch(() => undefined);
  }, [route.kind, account?.id, realtime.session?.id, canUseWorksheets]);

  async function logout() {
    await solo.transition(async () => {
      realtime.disconnect();
      await api("/auth/logout", { method: "POST" });
      solo.reset();
      setAccount(null);
      setNavigation([]);
      window.history.replaceState({}, "", "/login");
      window.location.hash = "";
    });
  }

  function endIdleSession() {
    solo.reset();
    realtime.disconnect();
    setAccount(null);
    setNavigation([]);
    setActingOptions([]);
    setSidebarOpen(false);
    setSidebarCollapsed(false);
    setIdleLogoutNotice(true);
    window.history.replaceState({}, "", "/login");
    setRoute(currentRoute());
  }

  function completeLogin(nextAccount: Account) {
    setIdleLogoutNotice(false);
    window.history.replaceState({}, "", `/${window.location.hash}`);
    setAccount(nextAccount);
  }

  async function changeActingAs(learnerId: string) {
    const change = async () => {
      const result = await api<{ account: Account }>(learnerId ? "/auth/acting-as" : "/auth/acting-as/stop", {
        method: "POST",
        ...(learnerId ? { body: JSON.stringify({ learnerId }) } : {}),
      });
      setAccount(result.account);
    };
    if (learnerId === (account?.actingAs?.id ?? "")) { await change(); return true; }
    return solo.transition(change);
  }

  if (passwordLinkToken) return <PasswordLinkView key={passwordLinkToken} token={passwordLinkToken} onClose={() => { setPasswordLinkToken(null); setAccount(null); window.history.replaceState({}, "", "/login"); setRoute(currentRoute()); }} />;
  if (passwordNotice) return <main className="password-link-page"><section className="password-card"><h1>Passwort geändert</h1><p>Sie wurden auf allen Geräten abgemeldet. Bitte melden Sie sich mit Ihrem neuen Passwort an.</p><button className="primary" onClick={() => { setPasswordNotice(false); window.history.replaceState({}, "", "/login"); setRoute(currentRoute()); }}>Zur Anmeldung</button></section></main>;
  if (loading) return <main className="app-loading"><img src="/brand/calepia_logo_mit_raupe.webp" alt="Calepia" /><span>Calepia startet …</span></main>;
  if (!account && route.kind === "legal") {
    return <main className="legal-public-page"><LegalPageView slug={route.slug} onBack={() => { window.location.hash = ""; }} /></main>;
  }
  if (!account && isLoginRequest(window.location.pathname, window.location.search, window.location.hash)) return <Login onLogin={completeLogin} notice={idleLogoutNotice ? "Du wurdest nach 15 Minuten ohne Eingabe abgemeldet. Melde dich erneut an, um weiterzumachen." : undefined} />;
  if (!account) return <PublicHome />;
  if (account.subscription?.restricted) {
    const manager = account.organizations.some(({ id, role }) => id === account.organizationId && ["OWNER", "MANAGER"].includes(role));
    return <RoleLabelsProvider labels={account.roleLabels}><main className="restricted-shell">
      <SessionActivity accountId={account.id} onExpired={endIdleSession} />
      <header className="restricted-header"><img src="/brand/calepia_logo_schrift.webp" alt="Calepia" /><nav aria-label="Kontoverwaltung">
        {manager && <button className="secondary" onClick={() => { window.location.hash = "#/management"; }}>Verwaltung</button>}
        <button className="secondary" onClick={() => { window.location.hash = "#/account"; }}>Mein Konto</button><LogoutButton onLogout={() => logout().catch(console.error)} />
      </nav></header>
      <p className="alert info">{manager ? "Der Lern- und Therapiebetrieb dieser Einrichtung ist derzeit pausiert. Kontenübersicht, Exporte, Löschung und notwendige Neuzuordnungen stehen weiterhin zur Verfügung." : "Calepia ist für deine Einrichtung gerade nicht verfügbar. Bitte wende dich an deine Einrichtung."}</p>
      {manager && route.kind !== "account" ? <Suspense fallback={<p>Verwaltung wird geladen …</p>}><ManagementPortal account={account} onAccountUpdated={setAccount} /></Suspense> : <><AccountInformation account={account} /><PasswordForm account={account} onSuccess={() => { realtime.disconnect(); setAccount(null); setPasswordNotice(true); }} /></>}
    </main></RoleLabelsProvider>;
  }

  function navigate(slug: string) {
    if (realtime.session?.participantRole === "PARTICIPANT") return;
    realtime.publishRoute(slug);
    window.location.hash = `#/page/${encodeURIComponent(slug)}`;
    setSidebarOpen(false);
    setSidebarCollapsed(false);
  }

  function navigateToSubject(id: string) {
    if (realtime.session?.participantRole === "PARTICIPANT") return;
    window.location.hash = `#/subject/${encodeURIComponent(id)}`;
    setSidebarOpen(false);
    setSidebarCollapsed(false);
  }

  function navigateToRoute(target: "learning" | "management" | "analysis" | "individual-exercises") {
    if (realtime.session?.participantRole === "PARTICIPANT") return;
    window.location.hash = `#/${target}`;
    setSidebarOpen(false);
    setSidebarCollapsed(false);
  }

  function navigateHome() {
    if (realtime.session?.participantRole === "PARTICIPANT") return;
    window.location.hash = "#/";
    setSidebarOpen(false);
    setSidebarCollapsed(false);
  }

  async function openLearnerAnalysis(learnerId: string, runId: string) {
    if (!await changeActingAs(learnerId)) return;
    window.location.hash = `#/analysis/${encodeURIComponent(runId)}`;
    setSidebarOpen(false);
  }

  return (
    <RoleLabelsProvider labels={account.roleLabels}><div className={`app-shell ${sidebarCollapsed ? "navigation-collapsed" : ""}${usesTherapistHeader ? " therapist-shell" : ""}`}>
      <SessionActivity accountId={account.id} onExpired={endIdleSession} />
      <header className={`app-header${usesTherapistHeader ? " therapist-header" : ""}`}>
        <button className="mobile-menu" aria-label={sidebarOpen ? "Navigation schließen" : "Navigation öffnen"} aria-expanded={sidebarOpen} onClick={() => setSidebarOpen((open) => !open)}>☰</button>
        <button className="brand-button" aria-label="Zur Startseite" onClick={navigateHome}>
          <img src="/brand/calepia_logo_mit_raupe.webp" alt="Calepia" />
          <span>Schritt für Schritt <b>zum Lernerfolg</b></span>
        </button>
        {usesTherapistHeader ? <TherapistHeaderActions
          account={account}
          roleText={roleText ?? ""}
          activeRoute={route.kind}
          actingOptions={actingOptions}
          canUseManagement={canUseManagement}
          canUseIndividualExercises={canUseIndividualExercises}
          canUseWorksheets={canUseWorksheets}
          canUseQualitativeAnalysis={canUseQualitativeAnalysis}
          syncActive={Boolean(realtime.session)}
          onNavigate={(target) => { window.location.hash = `#/${target}`; setSidebarOpen(false); }}
          onChangeActingAs={changeActingAs}
          onLogout={() => logout().catch(console.error)}
        /> : <div className="header-actions">
          {canUseCms && (
            <button className={route.kind === "cms" ? "header-link active" : "header-link"} onClick={() => { window.location.hash = "#/admin/cms"; setSidebarOpen(false); }}>
              Admin
            </button>
          )}
          {canUseManagement && (
            <button className={route.kind === "management" ? "header-link active" : "header-link"} onClick={() => { window.location.hash = "#/management"; setSidebarOpen(false); }}>
              Verwaltung
            </button>
          )}
          <button className="header-link" onClick={() => { window.location.hash = "#/account"; setSidebarOpen(false); }}>Mein Konto</button>
          {(account.modules.flashcards && !account.roles.includes("ADMIN")) && <button className="header-link" onClick={() => { window.location.hash = "#/flashcards"; setSidebarOpen(false); }}>Karteikarten</button>}
          {canUseIndividualExercises && (
            <button className={route.kind === "individual-exercises" ? "header-link active" : "header-link"} onClick={() => { window.location.hash = "#/individual-exercises"; setSidebarOpen(false); }}>
              Meine Übungen
            </button>
          )}
          {canUseWorksheets && <button className={`header-link worksheet-header-link${route.kind === "worksheets" ? " active" : ""}`} onClick={() => { window.location.hash = "#/worksheets"; setSidebarOpen(false); }}>Arbeitsblätter</button>}
          {canUseWorksheets && <button className="header-link" disabled={Boolean(realtime.session)} title={realtime.session ? "Bitte die Sync-Sitzung zuerst regulär beenden." : undefined} onClick={() => { window.location.hash = "#/whiteboard"; setSidebarOpen(false); }}>Whiteboard</button>}
          {canUseQualitativeAnalysis && (
            <button className={route.kind === "analysis" ? "header-link active" : "header-link"} onClick={() => { window.location.hash = "#/analysis"; setSidebarOpen(false); }}>
              Analyse
            </button>
          )}
          {actingOptions.length > 0 && (
            <label className="acting-as-control">
              <span>Bearbeiten als</span>
              <select value={account.actingAs?.id ?? ""} onChange={(event) => changeActingAs(event.target.value)}>
                <option value="">Eigenes Konto</option>
                {actingOptions.map((learner) => <option value={learner.id} key={learner.id}>{learner.displayName}</option>)}
              </select>
            </label>
          )}
          <div className="account-summary">
            <span className="avatar">{account.displayName.slice(0, 1).toUpperCase()}</span>
            <div>
              <strong>{account.displayName}</strong>
              <small>{roleText}</small>
            </div>
          </div>
          <LogoutButton onLogout={() => logout().catch(console.error)} />
        </div>}
      </header>

      <aside className={`app-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="mobile-account-controls">
          <div><strong>{account.displayName}</strong><small>{roleText}</small></div>
          <button className="sidebar-dashboard-link" onClick={() => { window.location.hash = "#/account"; setSidebarOpen(false); }}>Mein Konto</button>
          {actingOptions.length > 0 && (
            <label>
              Bearbeiten als
              <select value={account.actingAs?.id ?? ""} onChange={(event) => changeActingAs(event.target.value)}>
                <option value="">Eigenes Konto</option>
                {actingOptions.map((learner) => <option value={learner.id} key={learner.id}>{learner.displayName}</option>)}
              </select>
            </label>
          )}
          {(account.modules.flashcards && !account.roles.includes("ADMIN")) && <button className="sidebar-dashboard-link" onClick={() => { window.location.hash = "#/flashcards"; setSidebarOpen(false); }}>Karteikarten</button>}
          {(canUseCms || canUseManagement || canUseIndividualExercises || canUseQualitativeAnalysis || canUseWorksheets) && (
            <nav className="mobile-admin-navigation" aria-label="Administration">
              <span>Administration</span>
              {canUseCms && (
                <button className={route.kind === "cms" ? "active" : ""} onClick={() => { window.location.hash = "#/admin/cms"; setSidebarOpen(false); }}>
                  Admin
                </button>
              )}
              {canUseManagement && (
                <button className={route.kind === "management" ? "active" : ""} onClick={() => { window.location.hash = "#/management"; setSidebarOpen(false); }}>
                  Verwaltung
                </button>
              )}
              {canUseIndividualExercises && (
                <button className={route.kind === "individual-exercises" ? "active" : ""} onClick={() => { window.location.hash = "#/individual-exercises"; setSidebarOpen(false); }}>
                  Meine Übungen
                </button>
              )}
              {canUseWorksheets && <button className={route.kind === "worksheets" ? "active" : ""} onClick={() => { window.location.hash = "#/worksheets"; setSidebarOpen(false); }}>Arbeitsblätter</button>}
              {canUseWorksheets && <button disabled={Boolean(realtime.session)} onClick={() => { window.location.hash = "#/whiteboard"; setSidebarOpen(false); }}>Whiteboard</button>}
              {canUseQualitativeAnalysis && (
                <button className={route.kind === "analysis" ? "active" : ""} onClick={() => { window.location.hash = "#/analysis"; setSidebarOpen(false); }}>
                  Analyse
                </button>
              )}
            </nav>
          )}
          <LogoutButton onLogout={() => logout().catch(console.error)} />
        </div>
        {canUseLearningDashboard && <button className="sidebar-dashboard-link" onClick={() => { window.location.hash = "#/learning"; setSidebarOpen(false); }}>Mein Lernbereich</button>}
        {canUseWorksheets && <button className="sidebar-dashboard-link worksheet-tablet-link" onClick={() => { window.location.hash = "#/worksheets"; setSidebarOpen(false); }}>Arbeitsblätter</button>}
        {!account.roles.includes("ADMIN") && <SessionPanel account={account} />}
        <Navigation
          items={navigation}
          activeSlug={activeSlug}
          activeSubjectId={route.kind === "subject" ? route.id : undefined}
          onNavigate={navigate}
          onNavigateSubject={navigateToSubject}
        />
        <div className="sidebar-footer">
          <nav aria-label="Rechtliches">
            <a href="#/legal/impressum">Impressum</a>
            <a href="#/legal/datenschutz">Datenschutz</a>
            <a href="/THIRD-PARTY-NOTICES.txt">Lizenzen</a>
          </nav>
          <span>Plattform {technicalVersion("platform")}</span>
        </div>
      </aside>
      {sidebarOpen && <button className="sidebar-backdrop" aria-label="Navigation schließen" onClick={() => setSidebarOpen(false)} />}

      <main className={`app-main ${route.kind === "cms" || route.kind === "management" || route.kind === "analysis" || route.kind === "individual-exercises" || route.kind === "worksheets" ? "cms-main" : ""}`}>
        {solo.workspace && !solo.visible && solo.workspace.expiresAt - solo.workspace.serverNow <= 300000 && <aside className="alert info" role="status"><p>Der ausgeblendete Solo-Tisch läuft in weniger als fünf Minuten ab. Bitte öffnen und speichern.</p><button className="secondary" disabled={Boolean(realtime.session)} onClick={() => { window.location.hash = "#/whiteboard"; }}>Whiteboard öffnen</button></aside>}
        {route.kind === "account" ? <><AccountInformation account={account} /><PasswordForm account={account} onSuccess={() => { realtime.disconnect(); setAccount(null); setNavigation([]); setActingOptions([]); setPasswordNotice(true); window.history.replaceState({}, "", "/login"); }} /></> : route.kind === "flashcards" && !account.roles.includes("ADMIN") ? (
          <Suspense fallback={<p>Karteikarten werden geladen …</p>}><Flashcards key={`${account.id}:${account.actingAs?.id ?? "self"}:${account.modules.flashcards}`} account={account} realtimeActive={Boolean(realtime.session)} /></Suspense>
        ) : route.kind === "cms" && canUseCms ? (
          <Suspense fallback={<div className="loading-card">Adminbereich wird geladen …</div>}>
            <AdminCms />
          </Suspense>
        ) : route.kind === "management" && canUseManagement ? (
          <Suspense fallback={<div className="loading-card">Verwaltung wird geladen …</div>}>
            <ManagementPortal account={account} onAccountUpdated={setAccount} />
          </Suspense>
        ) : route.kind === "learning" && canUseLearningDashboard ? (
          <Suspense fallback={<div className="loading-card">Lernbereich wird geladen …</div>}>
            {route.section === "history" && (account.roles.includes("LEARNER") || "runId" in route) ? <LearnerHistory key={`${account.id}:${account.actingAs?.id ?? ""}:${"runId" in route ? route.runId : "list"}`} runId={"runId" in route ? route.runId : undefined} /> : <LearningDashboard account={account} navigation={navigation} focusHistory={route.section === "history"} />}
          </Suspense>
        ) : route.kind === "individual-exercises" && canUseIndividualExercises ? (
          <Suspense fallback={<div className="loading-card">Individuelle Übungen werden geladen …</div>}>
            <IndividualExercises />
          </Suspense>
        ) : route.kind === "whiteboard" && canUseWorksheets ? (
          <section className="card"><h1>Whiteboard</h1><p role="status">{realtime.session ? "Bitte die Sync-Sitzung zuerst regulär beenden. Eigene Vorlagen können im gemeinsamen Whiteboard geladen werden." : solo.notice || "Der Solo-Tisch wird geladen …"}</p>{!realtime.session && <button className="primary" onClick={() => { void solo.start(account.actingAs ? { kind: "learner", learnerId: account.actingAs.id } : { kind: "personal" }).catch(() => undefined); }}>Whiteboard öffnen</button>}</section>
        ) : route.kind === "worksheets" && canUseWorksheets ? (
          <Suspense fallback={<div className="loading-card">Arbeitsblätter werden geladen …</div>}><Worksheets key={`${account.id}:${account.organizationId}:${account.actingAs?.id ?? ""}`} /></Suspense>
        ) : route.kind === "analysis" && canUseQualitativeAnalysis ? (
          <Suspense fallback={<div className="loading-card">Analyse wird geladen …</div>}>
            <QualitativeAnalysis account={account} initialRunId={route.runId} />
          </Suspense>
        ) : route.kind === "legal" ? (
          <LegalPageView slug={route.slug} onBack={navigateHome} />
        ) : route.kind === "subject" ? (
          activeSubject
            ? <SubjectOverview subject={activeSubject} onNavigateToPage={navigate} />
            : <div className="loading-card">Fachansicht wird geladen …</div>
        ) : route.kind === "page" ? (
          <ContentPageView account={account} slug={route.slug} />
        ) : route.kind === "recommendation" ? (
          <RecommendationView key={`${account.id}:${account.actingAs?.id ?? ""}:${route.id}`} account={account} recommendationId={route.id} />
        ) : route.kind === "adaptive-assignment" ? (
          <AdaptiveAssignmentView account={account} assignmentId={route.id} />
        ) : route.kind === "individual-assignment" ? (
          <IndividualAssignmentView account={account} assignmentId={route.id} />
        ) : (
          <StartPage
            account={account}
            navigation={navigation}
            canUseLearningDashboard={canUseLearningDashboard}
            canUseManagement={canUseManagement}
            canUseQualitativeAnalysis={canUseQualitativeAnalysis}
            onNavigateToPage={navigate}
            onNavigateToRoute={navigateToRoute}
            onOpenLearnerAnalysis={openLearnerAnalysis}
          />
        )}
      </main>
      {(realtime.session || solo.visible) && <Suspense fallback={null}><WhiteboardWorkspace learnerOptions={actingOptions} /></Suspense>}
    </div></RoleLabelsProvider>
  );
}
